/**
 * SUB-5.4 · FR46. Which numbers mean the same thing in every account, and which
 * ones are arithmetic over rows one account configured for itself.
 *
 * ⚠️ THIS MODULE DECIDES WHAT IS SHOWN. IT BOUNDS NOTHING.
 *
 * The same standing rule as `mergedFilters.ts` and `accountAttribution.ts`: the
 * accounts a merged surface may span is `merged_scope_tenant_ids()` evaluated
 * inside the policy, and nothing here may ever be the reason a row is absent.
 * Withholding a metric removes a FIGURE, never a record.
 *
 * ---------------------------------------------------------------------------
 * THE RULE, IN ONE SENTENCE, AND IT IS NOT THE SAME AS FR36's
 *
 * A metric may appear on the roll-up when the quantity it produces is defined by
 * the platform, and only inside one account when it is keyed to a row that
 * account configured.
 *
 * ⚠️ CONSULTING EACH ACCOUNT'S CONFIGURATION IS NOT THE SAME AS NAMING ONE
 * ACCOUNT'S CONFIGURATION, and this is the distinction the whole registry turns
 * on. M1, "past the SLA their own account configured", reads
 * `stages.sla_settings` per account and is PORTABLE: the quantity is a count and
 * every account answers the same question with its own numbers. A metric grouped
 * by `current_stage_id` is ACCOUNT-SCOPED: the group keys are stage ids, and a
 * stage id exists in exactly one account. The first is why SUB-5.2 exists; the
 * second is why FR44's separate surface exists.
 *
 * ---------------------------------------------------------------------------
 * FR46 MEASURED, NOT ASSERTED. All 236 live `dashboard_widgets`, re-read
 * 2026-08-15. ⚠️ The brief's §4 table is reproduced here because it moved under
 * one wrong query before it was read correctly: `groupBy` lives at
 * `config.visualization.groupBy`, not at the top level, and looking for it at the
 * top level reports zero.
 *
 * | | Widgets | Share |
 * |---|---|---|
 * | Total rows | 236 | |
 * | …carrying a `dataSource` (the rest are text widgets) | 229 | 97% |
 * | Name at least one pipeline id in `filters.pipelineIds` | **212** | 90% |
 * | Name at least one stage id in `filters.stageIds` | 90 | 38% |
 * | Name at least one project field | 59 | 25% |
 * | Group by `current_stage_id` | **39** | 17% |
 * | Group by `assigned_sales_rep_id` | 21 | 9% |
 * | `dataSource: 'projects'` | 229 | 100% of data widgets |
 *
 * ⚠️ **Counting the KEY rather than a non-empty value overstates it.** 110 rows
 * carry a `stageIds` key and only 90 put anything in it; 93 carry
 * `projectFields` and 59 use it. The figures above are the non-empty ones,
 * because an empty array names no stage and constrains nothing.
 *
 * **Nine in ten existing widgets name a pipeline id**, and a pipeline id belongs
 * to one account. So "the same widgets, wider" is not available even in
 * principle, and FR44's separate surface is a consequence of the data rather
 * than a design preference. Only two group-bys are in use anywhere and one of
 * them is a stage.
 */

import {
  ACCOUNT_SCOPED_SECTION_LABEL,
  PORTABLE_SECTION_LABEL,
} from './mergedFilters';
import { CURRENCY_COVERAGE_BASIS, type CoverageBasis } from './metricCoverage';

/**
 * The two section headings are SHARED with the filter registry on purpose. D21
 * chose the wording once (C3), and a roll-up that explains the divergence rule
 * with different words from the filter panel teaches the user that they are two
 * different rules. They are one rule about two kinds of control.
 */
export { ACCOUNT_SCOPED_SECTION_LABEL, PORTABLE_SECTION_LABEL };

export type MetricPortability = 'portable' | 'account_scoped';

/**
 * AC2's list, verbatim from FR46: "counts, ages, durations, rates or currency
 * amounts". A portable metric has to be one of these, and the test file enforces
 * that, so a new metric cannot be added to the roll-up without someone naming
 * which kind of quantity makes it safe to add up.
 */
export type Fr46Quantity = 'count' | 'age' | 'duration' | 'rate' | 'currency';

/**
 * AC1's list, verbatim: "a specific pipeline, stage or checklist identity".
 * An account-scoped metric has to name which of the three it is keyed to, so
 * "it felt per-account" is not an available justification.
 */
export type ConfiguredIdentity = 'pipeline' | 'stage' | 'checklist';

export interface MergedMetricRule {
  /** Stable id, used by a surface to ask for a metric by name. */
  id: string;
  /** What the figure is called where it is shown. */
  label: string;
  portability: MetricPortability;
  /** Which of FR46's quantities makes it aggregable. Portable rules only. */
  fr46Quantity?: Fr46Quantity;
  /** Which configured identity keys it. Account-scoped rules only. */
  dependsOn?: ConfiguredIdentity;
  /**
   * The UI states WHY. Account-scoped rules only, and required for them: a
   * figure that is simply absent teaches nothing, and the roll-up's only
   * explanation of the divergence rule is this string next to its heading.
   */
  reason?: string;
  /**
   * ⚠️ THE AWKWARD CASE, AND IT IS REAL RATHER THAN HYPOTHETICAL. A metric whose
   * QUANTITY travels but whose LABELS do not. `assigned_sales_rep_id` is the
   * live instance: it groups 21 real widgets, a person is not one of FR46's
   * three configured identities, and the count of their open work is a count in
   * any account. But `users_select` is
   * `id = auth.uid() OR tenant_id = get_current_user_tenant_id() OR a user_roles
   * row in that same account`, so in merged scope a sub-account's own staff
   * CANNOT BE NAMED (brief §3, D37).
   *
   * Forcing that into `account_scoped` would be wrong (FR46 does not say a
   * person is configuration) and forcing it into plain `portable` would ship a
   * chart of blank labels. So it is portable AND it carries the caveat, and any
   * surface rendering it has to say what it does about the names.
   */
  renderCaveat?: string;
  /**
   * ⚠️ THE SECOND AWKWARD CASE, AND IT IS THE ONE F-99 FILED. A metric whose
   * quantity travels, whose labels travel, and whose ANSWER still means nothing
   * without saying what it was computed over.
   *
   * `system_cost_total` is the live instance. It is portable on every axis this
   * registry had — currency is on FR46's list, the column is a platform column,
   * no configured identity keys it — and summing it across the live accounts
   * would print a headline computed over 16% of Solerro and 0% of Testing.
   * Portability was never the question; the registry simply had no way to ask
   * the other one.
   *
   * A rule carrying a basis may NOT be rendered as a bare number. See
   * `metricCoverage.ts`, where the pairing is enforced by the type rather than
   * by this comment.
   */
  coverageBasis?: CoverageBasis;
}

/**
 * ⚠️ LOAD-BEARING COPY, NOT CHROME. Alongside the filter panel's note, this is
 * the only place the roll-up explains why a number it could obviously compute is
 * not on the page. `mergedMetrics.test.ts` asserts on it so a tidy-up fails a
 * test.
 */
export const ACCOUNT_SCOPED_METRICS_NOTE =
  'These figures count things each account sets up for itself, so adding them together would produce a number that is not true anywhere. Open one account to see them.';

/**
 * Everything the roll-up knows how to show, and everything it knows to refuse.
 *
 * ⚠️ ABSENCE FROM THIS LIST IS NOT PERMISSION. `partitionMetricsForScope` treats
 * an unregistered id as account-scoped, which is D35 applied to metrics: a
 * figure added to a surface without a rule is withheld rather than shown on the
 * strength of nobody having classified it. The safe direction is to withhold
 * something that would have travelled; showing something that does not is the
 * failure this story exists to stop.
 */
export const MERGED_METRIC_RULES: readonly MergedMetricRule[] = [
  // ---- The four launch metrics (FR48, SUB-5.3 builds them) -----------------
  {
    id: 'past_sla',
    label: 'Past SLA',
    portability: 'portable',
    // A count. Each account is asked the same question and answers it with its
    // own thresholds, which is SUB-5.2's whole point: `sla_breach_days` reads
    // per account and D45 takes that account's maximum. No stage is named.
    fr46Quantity: 'count',
  },
  {
    id: 'no_stage_movement',
    label: 'No stage movement',
    portability: 'portable',
    // A duration over `projects.stage_changed_at`, which is a platform column on
    // every project. It does not care which stage, only how long since the last
    // change, so it is the one launch metric with no configuration in it at all.
    fr46Quantity: 'duration',
  },
  {
    id: 'open_work_by_member',
    label: 'Open work by team member',
    portability: 'portable',
    fr46Quantity: 'count',
    renderCaveat:
      'A sub-account\'s own staff cannot be named in merged scope, because users_select is single-tenant. A surface showing this must either resolve names through a gated read or label the group by account rather than by person.',
  },
  {
    id: 'active_projects',
    label: 'Active projects',
    portability: 'portable',
    // A count, and "active" is `projectStatusVocabulary.ts`'s canonical bucket
    // rather than a raw `project_status` string. ⚠️ That distinction is F-45:
    // the raw column is free text and a bucket is the only portable reading.
    fr46Quantity: 'count',
  },

  // ---- The roll-up's OTHER figures, registered 2026-08-17 ------------------
  //
  // ⚠️ These four render on `/app/portfolio` and had no rule, which made the
  // registry's headline guarantee false: `partitionMetricsForScope` fails closed
  // on an unregistered id, so each of them WAS classified account-scoped, and
  // the page showed them anyway because it never called the registry. A rule
  // nothing consults cannot withhold anything. They are registered here and the
  // page now asks.
  {
    id: 'worst_overrun',
    label: 'Worst overrun',
    portability: 'portable',
    // A duration past each account's OWN threshold, which is the same reading
    // `past_sla` is portable under: `sla_breach_days` resolves per account, so
    // the quantity compared across accounts is "days late", not "days in stage
    // X". ⚠️ It is the primary sort key of D47's ranking, so withholding it
    // would have removed the column the table orders by.
    fr46Quantity: 'duration',
  },
  {
    id: 'unmeasured_projects',
    label: 'Not measured',
    portability: 'portable',
    // A count of projects whose stage has no SLA configured. The ABSENCE of
    // configuration is itself configuration-independent: every account is asked
    // the same yes/no question and no threshold is compared.
    fr46Quantity: 'count',
  },
  {
    id: 'oldest_untouched',
    label: 'Oldest untouched',
    portability: 'portable',
    // The same platform column as `no_stage_movement`, reduced with max instead
    // of a count. No stage is named.
    fr46Quantity: 'duration',
  },
  {
    id: 'active_projects_new',
    label: 'New in 30 days',
    portability: 'portable',
    // A count over `projects.created_at`, a platform column, intersected with
    // the canonical active bucket that `active_projects` already justifies.
    fr46Quantity: 'count',
  },

  // ---- The generic quantities the existing widgets already aggregate -------
  {
    id: 'project_count',
    label: 'Projects',
    portability: 'portable',
    fr46Quantity: 'count',
  },
  {
    id: 'system_cost_total',
    label: 'System cost',
    portability: 'portable',
    // FR46 names currency explicitly. `sum(system_cost)` is live on a widget
    // today and the column is a platform column, not a configured one. That was
    // never in doubt and was never what F-99 disputed.
    fr46Quantity: 'currency',
    // F-99, answered by Allan 2026-08-18: the figure travels, and it always
    // travels with the rows it was computed over. Coverage is 16% in Solerro and
    // 0% in Testing, so a bare sum is a wrong headline rather than a rough one.
    coverageBasis: CURRENCY_COVERAGE_BASIS,
  },
  {
    id: 'age_in_stage',
    label: 'Age in current stage',
    portability: 'portable',
    fr46Quantity: 'age',
  },
  {
    id: 'completion_rate',
    label: 'Completion rate',
    portability: 'portable',
    // The `percentage` aggregation is live on 13 widgets. A ratio of two
    // portable counts is itself portable; a ratio whose numerator or denominator
    // is account-scoped is not, and the surface computing it has to check both
    // rather than trusting this entry.
    fr46Quantity: 'rate',
    // ⚠️ F-99 FILED THIS ENTRY AS A CONTRADICTION, AND IT WAS ONE. It was
    // registered portable with NO implementation, no numerator and no
    // denominator, and its only plausible reading — checklist completion — is
    // refused as account-scoped by `by_checklist_item` twelve rules below. So it
    // either meant something unbuilt or disagreed with its own sibling.
    //
    // Defined 2026-08-18 (Allan) as `won / (won + open)` over the new
    // `stages.is_final` / `stages.final_outcome` flags. See `completionRate.ts`
    // for why that is portable under this registry's own rule and why a single
    // boolean was not enough: 775 live projects sit in a "Cancelled" stage
    // against 467 in "Project Complete", and lumping them together would have
    // reported cancelled work as completed work.
    //
    // It carries a caveat rather than a coverage basis because what can be
    // missing is CONFIGURATION, not data: an account that has marked no stage
    // final is excluded from a merged figure rather than counted as 0%.
    renderCaveat:
      'An account that has marked no stage as a finish is excluded from this figure rather than counted as zero, and a surface showing it must name the accounts it left out. Cancelled work is excluded from both sides of the ratio.',
  },

  // ---- Keyed to a configured identity. AC1's refusals. ---------------------
  {
    id: 'by_stage',
    label: 'Projects by stage',
    portability: 'account_scoped',
    dependsOn: 'stage',
    reason:
      'Stages are named and ordered per account, so the same stage name is a different stage in each and the counts cannot be added together.',
  },
  {
    id: 'by_pipeline',
    label: 'Projects by pipeline',
    portability: 'account_scoped',
    dependsOn: 'pipeline',
    reason:
      'Pipelines belong to one account, so a pipeline from one account matches nothing in another.',
  },
  {
    id: 'by_checklist_item',
    label: 'Checklist completion',
    portability: 'account_scoped',
    dependsOn: 'checklist',
    reason:
      'Checklist items are defined per account, so the same milestone is a different item in each.',
  },
  {
    id: 'stage_conversion',
    label: 'Stage conversion',
    portability: 'account_scoped',
    dependsOn: 'stage',
    reason:
      'A conversion rate between two named stages needs both stages to exist in every account, and they do not.',
  },
  {
    id: 'time_in_named_stage',
    label: 'Time in a named stage',
    portability: 'account_scoped',
    dependsOn: 'stage',
    // ⚠️ A duration is on FR46's portable list and this one is still withheld,
    // which is the case that shows the two halves of FR46 are independent. AC2
    // constrains WHAT is aggregated; AC1 constrains WHAT IT IS KEYED TO. A
    // duration keyed to a stage id fails AC1 while passing AC2.
    reason:
      'This measures time in one particular stage, and that stage exists in one account only.',
  },
];

/** The rule for an id, or undefined if nobody has classified it. */
export function metricRule(id: string): MergedMetricRule | undefined {
  return MERGED_METRIC_RULES.find((rule) => rule.id === id);
}

/**
 * The rule an unregistered metric gets. Withheld, and it says why in the terms
 * that are actually true: nobody has established that it travels.
 */
function unclassified(id: string): MergedMetricRule {
  return {
    id,
    label: id,
    portability: 'account_scoped',
    reason:
      'This figure has not been checked against the accounts in scope, so it is shown inside one account only.',
  };
}

export interface MetricPartition {
  /** Shown on the roll-up, in the order the surface asked for. */
  shown: MergedMetricRule[];
  /** Named, absent and explained. Empty whenever scope is a single account. */
  withheld: MergedMetricRule[];
}

/**
 * AC1 and AC2 in one call: what the roll-up may show, and what it must instead
 * name and explain.
 *
 * In single-account scope nothing is withheld, because every metric means
 * exactly one thing there. That is FR46's "is per-account only": the refusal is
 * a property of the merged surface, not a property of the metric.
 */
export function partitionMetricsForScope(
  ids: readonly string[],
  merged: boolean,
): MetricPartition {
  const shown: MergedMetricRule[] = [];
  const withheld: MergedMetricRule[] = [];

  for (const id of ids) {
    const rule = metricRule(id) ?? unclassified(id);
    if (!merged || rule.portability === 'portable') shown.push(rule);
    else withheld.push(rule);
  }

  return { shown, withheld };
}

/**
 * Whether a figure must be shown with the rows it was computed over.
 *
 * ⚠️ Fails CLOSED on an unregistered id in the same direction as everything else
 * here: an unknown metric is treated as needing its denominator. Showing a
 * qualified figure that did not need qualifying costs a caption; showing a bare
 * one that needed it is F-99.
 */
export function metricCoverageBasis(id: string): CoverageBasis | undefined {
  const rule = metricRule(id);
  if (!rule) return CURRENCY_COVERAGE_BASIS;
  return rule.coverageBasis;
}

/** Every metric that may not be rendered as a bare number. */
export function metricsRequiringCoverage(): MergedMetricRule[] {
  return MERGED_METRIC_RULES.filter((rule) => Boolean(rule.coverageBasis));
}

/** Every metric that travels. */
export function portableMetricIds(): string[] {
  return MERGED_METRIC_RULES.filter((rule) => rule.portability === 'portable').map((rule) => rule.id);
}

/** Every metric the roll-up must withhold, whether or not it asked for it. */
export function withheldInMergedScope(): MergedMetricRule[] {
  return MERGED_METRIC_RULES.filter((rule) => rule.portability === 'account_scoped');
}

/**
 * Every portable metric whose labels will not survive the boundary even though
 * its number will. A surface rendering one of these has to say what it does
 * about the names; see `renderCaveat` for why this is not just a warning.
 */
export function portableWithRenderCaveat(): MergedMetricRule[] {
  return MERGED_METRIC_RULES.filter(
    (rule) => rule.portability === 'portable' && Boolean(rule.renderCaveat),
  );
}

/**
 * AC2 as a callable check, for a surface composing a metric out of others (the
 * live `percentage` aggregation does exactly this). A derived figure is portable
 * only when every input is.
 *
 * ⚠️ Deliberately fails closed on an unregistered input, for the same reason
 * `partitionMetricsForScope` does.
 */
export function derivedMetricIsPortable(inputIds: readonly string[]): boolean {
  if (inputIds.length === 0) return false;
  return inputIds.every((id) => metricRule(id)?.portability === 'portable');
}
